"use client";

import { useEffect, useId, useState } from "react";
import { Input } from "@/components/ui/input";
import { useDebouncedSearch } from "@/lib/use-debounced-search";
import { cn } from "@/lib/utils";
import type { SecurityRef } from "@/lib/data/types";

/**
 * Schlanke Ticker-Auswahl fuers Depot-Builder-Formular - onSelect() statt Navigation,
 * keine Lead-Gate-Logik, sonst derselbe useDebouncedSearch-Hook wie
 * components/search/search-hero.tsx (vorher zwei eigenstaendige Kopien desselben
 * Debounce/Fetch-Musters, siehe Hook-Docstring fuer den dadurch verdoppelten Bug).
 */
export function TickerCombobox({
  onSelect,
  value,
  onQueryChange,
  placeholder,
}: {
  onSelect: (ref: SecurityRef) => void;
  /** Optionaler kontrollierter Anzeigewert - ohne diesen Prop bleibt die Komponente wie
   * bisher unkontrolliert (eigener interner State). Fuer Aufrufer, die den getippten/
   * ausgewaehlten Text auch von aussen setzen koennen muessen (z.B. VorabpauschaleCalculator,
   * wo ein "aus Depot uebernehmen"-Picker denselben Feldwert setzt). */
  value?: string;
  /** Wird bei jedem Tastendruck aufgerufen, zusaetzlich zu onSelect bei Listenauswahl - fuer
   * Aufrufer, die auch frei eingetippten Text ohne Listenauswahl uebernehmen wollen. */
  onQueryChange?: (query: string) => void;
  placeholder?: string;
}) {
  const [internalQuery, setInternalQuery] = useState("");
  const query = value !== undefined ? value : internalQuery;
  function setQuery(next: string) {
    setInternalQuery(next);
    onQueryChange?.(next);
  }
  const suggestions = useDebouncedSearch(query);
  const [isOpen, setIsOpen] = useState(false);
  // Tastatur-Bedienbarkeit (Barrierefreiheits-Audit, 2026-08-10) - gleiches Muster wie
  // search-hero.tsx: activeIndex trackt die per Pfeiltasten markierte Vorschlagszeile,
  // vorher gab es hier ausschliesslich onMouseDown, ein Tastatur-Nutzer konnte trotz
  // role="combobox"/"listbox"/"option" keinen Vorschlag auswaehlen.
  const [activeIndex, setActiveIndex] = useState(-1);

  useEffect(() => {
    // setState nicht synchron im Effect-Body (react-hooks/set-state-in-effect).
    const id = setTimeout(() => setActiveIndex(-1), 0);
    return () => clearTimeout(id);
  }, [suggestions]);

  function handleSelect(ref: SecurityRef) {
    setIsOpen(false);
    setQuery(`${ref.name} (${ref.symbol})`);
    onSelect(ref);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (!isOpen || suggestions.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      handleSelect(suggestions[activeIndex] ?? suggestions[0]);
    } else if (event.key === "Escape") {
      setIsOpen(false);
    }
  }

  const showDropdown = isOpen && query.trim().length >= 2 && suggestions.length > 0;
  const listboxId = useId();

  return (
    <div className="relative">
      <Input
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setTimeout(() => setIsOpen(false), 120)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder ?? "Name, ISIN oder WKN …"}
        aria-label="Aktie auswählen"
        role="combobox"
        aria-expanded={showDropdown}
        aria-controls={listboxId}
        aria-autocomplete="list"
        className="bg-card"
      />
      {showDropdown && (
        <ul
          id={listboxId}
          role="listbox"
          className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-border bg-card shadow-md"
        >
          {suggestions.map((s, i) => (
            <li
              key={`${s.symbol}-${s.isin ?? i}`}
              role="option"
              aria-selected={i === activeIndex}
              onMouseDown={() => handleSelect(s)}
              className={cn("cursor-pointer px-3 py-2 text-sm", i === activeIndex ? "bg-muted" : "hover:bg-muted")}
            >
              <span className="font-medium text-ink">{s.name}</span>
              <span className="ml-2 text-muted-foreground">{s.symbol}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
